import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ChevronDown } from 'lucide-react';
import { useMemo } from 'react';
import { z } from 'zod';

import { InfoPage } from '@/components/info-page';
import { createTableRowSelectionCell, EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StudentStatusBadge } from '@/components/students/student-status-badge';
import { StudentBulkUpdate } from '@/components/students/student-bulk-update';

import { EmptyArray, getDroppedStudents } from '@/lib/api-calls';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing';
import { DEFAULT_PAGE_SIZE, paginationRequestSchema } from '@/lib/pagination';
import { usePaginatedFilters } from '@/lib/filtering';
import { getUpcomingSessions, isAirAdmin } from '@airreading/common';

import { useAllSchools, useOrgOptions, useSchoolAndOrgOptions } from '@/lib/api-call-hooks';
import { useErrorToast } from '@/lib/error-handling';
import { airColumnHelper } from '@/lib/tables';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { calculateAttendanceRate, getStudentYearOptions } from '@/lib/student-utils';
import { getDistrictColumn } from '@/lib/district-utils';

import {
  FullStudent,
  GetStudentsSortableColumn,
  StudentStatus,
  StudentYearColorMap,
  getStudentsSortOrderSchema,
  getStudentsSortableColumnSchema,
  studentYearOption,
  studentYearSchema,
} from '@airreading/common/data';
import { formatInstant } from '@airreading/common';
import { CreateStudent } from '@/routes/_layout/users/-components/create-student';
import { CreateStudentsByCsv } from '@/routes/_layout/users/-components/create-students-by-csv';
import { useTimeZone } from '@/auth/useTimeZone';
import { studentsTabs } from '../-components/tabs';
import { useSorting } from '@/lib/sorting';

const studentsSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  schools: z.array(z.string()).optional(),
  orgs: z.array(z.string()).optional(),
  grades: z.array(studentYearSchema).optional(),
  sortBy: getStudentsSortOrderSchema.optional(),
});

export const Route = createFileRoute('/_layout/students/dropped/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: StudentsDroppedIndex,
  validateSearch: studentsSearchSchema,
});

const col = airColumnHelper<FullStudent>(getStudentsSortableColumnSchema);

const buildColumns = (timeZone: string, schoolIdToDistrictName: Record<string, string>) => [
  createTableRowSelectionCell(col),
  col.accessor((row) => row.id, {
    id: 'fullNameOrEmail',
    header: 'Name',
    cell: ({ row, getValue }) => (
      <Link
        to={'/students/$studentId'}
        params={{ studentId: getValue() }}
        search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
      >
        {row.original.fullNameOrEmail}
      </Link>
    ),
  }),
  col.accessor('email', {
    id: 'email',
    header: 'Email',
  }),
  col.accessor('school', {
    id: 'district',
    header: 'District',
    cell: ({ getValue }) => {
      const school = getValue();
      const districtName = school?.id ? schoolIdToDistrictName[school.id] : null;
      if (!districtName) return <span>Unknown</span>;
      return school
        ? getDistrictColumn({
            orgId: school.orgId,
            orgName: districtName,
          })
        : null;
    },
  }),
  col.accessor((row) => row, {
    id: 'school',
    header: 'School',
    enableSorting: false,
    cell: ({ getValue }) => {
      const school = getValue()?.school;
      return (
        school && (
          <Link
            to={'/orgs/schools/$schoolId/classes'}
            params={{ schoolId: school.id }}
            search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
            className="text-blue-600"
          >
            {school.name}
          </Link>
        )
      );
    },
  }),
  col.accessor('grade', {
    id: 'grade',
    header: 'Grade',
    cell: ({ getValue }) => {
      const grade = getValue();
      if (!grade) return studentYearOption(grade);
      const backgroundColor = StudentYearColorMap[grade];
      return (
        <div className="min-w-[80px] flex justify-start">
          <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
            {studentYearOption(grade)}
          </span>
        </div>
      );
    },
  }),
  col.accessor('status', {
    id: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      return (
        <div className="flex gap-x-1">
          <StudentStatusBadge status={getValue()} />
        </div>
      );
    },
  }),
  col.accessor('totalFutureSessions', {
    id: 'totalFutureSessions',
    header: 'Total future sessions',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ?? 0;
    },
  }),
  col.accessor('studentSessions', {
    header: 'Next session date',
    id: 'nextSessionDate',
    cell: ({ getValue }) => {
      const { nextSession } = getUpcomingSessions({ sessions: getValue() ?? [], timeZone });
      return nextSession && formatInstant(nextSession.startInstant, timeZone).formattedDate;
    },
  }),
  col.accessor('studentSessions', {
    id: 'attendance-all-past-sessions-last-14-days',
    header: 'Attendance - past 14 days',
    cell: ({ row }) => {
      const sessionFeedbacks = row.original.sessionFeedbacks;
      return `${calculateAttendanceRate(sessionFeedbacks, 14)}%`;
    },
  }),
];

function StudentsDroppedIndex() {
  const { timeZone } = useTimeZone();
  const { data: allSchools } = useAllSchools();

  const schoolIdToDistrictName = Object.fromEntries(allSchools.map((school) => [school.id, school.org.name]));

  const columns = useMemo(() => buildColumns(timeZone, schoolIdToDistrictName), [timeZone, schoolIdToDistrictName]);
  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetStudentsSortableColumn>([sortBy, didChangeSortBy]);

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[userNameQuery, activeUserNameQuery], setUserNameQuery],
      schools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      orgs: [[selectedOrgs, activeSelectedOrgs], setSelectedOrgs],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'schools', 'orgs', 'grades']);

  const allOrganizationOptions = useOrgOptions();
  const allSchoolOptions = useSchoolAndOrgOptions({
    orgIds: activeSelectedOrgs ?? [],
  });
  const {
    data: response,
    isPending,
    error,
    refetch,
  } = useQuery(
    getDroppedStudents({
      pageIndex,
      pageSize,
      filters: {
        query: activeUserNameQuery,
        schools: activeSelectedSchools,
        orgs: activeSelectedOrgs,
        grades: activeSelectedGrades,
      },
      sortBy,
    }),
  );

  const students = useMemo(() => response?.students || EmptyArray, [response]);
  useErrorToast(error, 'Error loading students. Please try again.');

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<FullStudent>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => students.find((student) => student.id === id),
    });

  const onSuccess = () => {
    resetAllRowSelection();
    refetch();
  };

  const table = useReactTable({
    data: students,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting,
      rowSelection,
    },
    rowCount: response?.total || 0,
    onRowSelectionChange,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    ...tableSortingOptions,
    enableColumnResizing: true,
  });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search users by name or email"
          value={userNameQuery ?? ''}
          onChange={(event) => {
            setUserNameQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="Districts"
          options={allOrganizationOptions}
          selectedValues={selectedOrgs ?? []}
          onChange={setSelectedOrgs}
        />
        <TableFilterDropdown
          title="Schools"
          options={allSchoolOptions}
          selectedValues={selectedSchools ?? []}
          onChange={setSelectedSchools}
        />
        <TableFilterDropdown
          title="Grades"
          options={getStudentYearOptions()}
          selectedValues={selectedGrades ?? []}
          onChange={setSelectedGrades}
        />

        <TableFilterResetButton show={true} onClick={resetFilters} />
      </>
    ),
    [
      userNameQuery,
      setUserNameQuery,
      allSchoolOptions,
      selectedSchools,
      setSelectedSchools,
      allOrganizationOptions,
      selectedOrgs,
      setSelectedOrgs,
      selectedGrades,
      setSelectedGrades,
      resetFilters,
    ],
  );

  const bulkActionsMenuItems = (
    <>
      <StudentBulkUpdate
        confirmMessage="Are you sure you want to re-enroll"
        invalidMessage="Only dropped students can be selected."
        options={{ status: StudentStatus.enrolled }}
        onSuccess={onSuccess}
        students={rowDataSelection}
        title="Re-enroll students"
        validation={(student) => student.status === StudentStatus.dropped}
      />
    </>
  );

  const topBarRightComponents = (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button className="ml-auto hidden h-8 lg:flex">
          Add students
          <ChevronDown className="mr-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[150px]">
        <CreateStudent
          onSuccess={refetch}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Add a student</DropdownMenuItem>}
        />
        <CreateStudentsByCsv
          onSuccess={refetch}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Import from CSV</DropdownMenuItem>}
          from="dropped"
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <InfoPage headerText="Students" tabs={studentsTabs('Dropped')}>
      <EntityTable
        table={table}
        showPagination={true}
        isLoading={isPending}
        hasError={!!error}
        emptyState={'No students found'}
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents }}
        bulkActionsMenuItems={bulkActionsMenuItems}
        topBarRightComponents={topBarRightComponents}
      />
    </InfoPage>
  );
}
